import { Link, Outlet, ScrollRestoration } from 'react-router-dom';
import { FeedbackFooter } from '../modules/feedback';
import { YourTurnProbes } from '../modules/games';
import { InstallBanner } from '../onboarding/InstallBanner';
import { UpdateToast } from '../pwa/UpdateToast';
import { BottomNav } from './BottomNav';
import { NavIcon } from './icons/NavIcon';
import { SyncStatusIndicator } from './SyncStatusIndicator';
import { TopNav } from './TopNav';

/**
 * Real app chrome (replaces the WS-0 scaffold's inline nav in App.tsx). Bottom tabs on
 * mobile; a traditional top nav bar on desktop (owner call 2026-07-31 — supersedes the
 * earlier sidebar variant, see docs/DECISIONS.md). Padded for iOS safe areas, with a
 * persistent sync-status slot and the update-available toast mounted once at the root.
 */
export function AppShell(): JSX.Element {
  return (
    <div className="flex min-h-dvh flex-col bg-bg text-fg">
      <header className="border-b border-line bg-surface pt-[env(safe-area-inset-top)]">
        <div className="mx-auto flex max-w-5xl items-center justify-between gap-6 px-4 py-4 lg:py-0">
          <div className="flex min-w-0 items-center gap-6">
            <Link
              to="/digest"
              className="shrink-0 text-xl font-semibold tracking-tight text-fg no-underline lg:font-display lg:text-xl"
            >
              AwwYeah
            </Link>
            <TopNav />
          </div>
          <div className="flex shrink-0 items-center gap-3">
            <SyncStatusIndicator />
            {/* Desktop-only settings entry point (owner call, 2026-07-31): mobile keeps
                Settings as its 5th bottom tab, so this would be a redundant second control
                there — see docs/DECISIONS.md. */}
            <Link
              to="/settings"
              aria-label="Settings"
              className="hidden h-9 w-9 items-center justify-center rounded-full text-fg-2 ease-app hover:bg-raised hover:text-fg lg:flex"
            >
              {/* Same resolver the tabs use, so the gear follows the icon-set preference too. */}
              <NavIcon moduleId="settings" emoji="⚙️" size={18} />
            </Link>
          </div>
        </div>
      </header>

      <InstallBanner />

      <main className="mx-auto w-full max-w-3xl flex-1 px-4 pb-24 lg:max-w-5xl lg:pb-8">
        <Outlet />
        {/* "Ideas for AwwYeah? Tell Dan" (2026-11-03): a quiet link at the end of every
            screen's content — inside <main>, so the mobile bottom-nav padding below keeps
            it clear of the tabs. Renders nothing for the recipient or when signed out. */}
        <FeedbackFooter />
        {/* Reset scroll on navigation (restore on back/forward) — without this, a detail
            page inherits the list page's scroll position (reported on recipes, 2026-08-04). */}
        <ScrollRestoration />
      </main>

      <BottomNav />
      <UpdateToast />
      <YourTurnProbes />
    </div>
  );
}
